import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useEntityScreeningLenses } from 'hooks/useEntityScreeningLenses';

import { EntityHitFilters, parseMatchScoreFilter } from '../Tables/entityHitList';

interface EntityHitsFilterModalProps {
  open: boolean;
  onClose: () => void;
  filters: EntityHitFilters;
  lensOptions: string[];
  listNameOptions: string[];
  entityCountryOptions: string[];
  onApply: (filters: EntityHitFilters) => void;
}

const EntityHitsFilterModal: React.FC<EntityHitsFilterModalProps> = ({
  open,
  onClose,
  filters,
  lensOptions,
  listNameOptions,
  entityCountryOptions,
  onApply,
}) => {
  const { lenses: vocabularyLenses } = useEntityScreeningLenses(open);
  const allLensOptions = Array.from(
    new Set([...lensOptions, ...vocabularyLenses, ...filters.lenses]),
  );
  const [selectedLenses, setSelectedLenses] = useState<string[]>(filters.lenses);
  const [selectedListNames, setSelectedListNames] = useState<string[]>(
    filters.listNames,
  );
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    filters.entityCountries,
  );
  const [minScore, setMinScore] = useState(
    filters.matchScoreMin === null ? '' : String(filters.matchScoreMin),
  );
  const [maxScore, setMaxScore] = useState(
    filters.matchScoreMax === null ? '' : String(filters.matchScoreMax),
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedLenses(filters.lenses);
    setSelectedListNames(filters.listNames);
    setSelectedCountries(filters.entityCountries);
    setMinScore(
      filters.matchScoreMin === null ? '' : String(filters.matchScoreMin),
    );
    setMaxScore(
      filters.matchScoreMax === null ? '' : String(filters.matchScoreMax),
    );
  }, [open, filters]);

  const handleToggle = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((previous) =>
      previous.includes(value)
        ? previous.filter((item) => item !== value)
        : [...previous, value],
    );
  };

  const handleApply = () => {
    onApply({
      lenses: selectedLenses,
      listNames: selectedListNames,
      entityCountries: selectedCountries,
      matchScoreMin: parseMatchScoreFilter(minScore),
      matchScoreMax: parseMatchScoreFilter(maxScore),
    });
    onClose();
  };

  const handleClear = () => {
    setSelectedLenses([]);
    setSelectedListNames([]);
    setSelectedCountries([]);
    setMinScore('');
    setMaxScore('');
  };

  const renderOption = (
    option: string,
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <div
      key={option}
      className='bill-container-fit-screen-content-row-checkbox'
      onClick={onClick}
    >
      <div className='bill-container-fit-screen-content-row-input'>
        <div
          className={`bill-container-fit-screen-content-row-input-icon ${
            isSelected ? 'selected' : ''
          }`}
        >
          <div
            className={`bill-container-fit-screen-content-row-input-icon-rnd ${
              isSelected ? 'selected' : ''
            }`}
          />
        </div>
      </div>
      <div className='bill-container-fit-screen-content-row-checkbox-text'>
        <p>{option}</p>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div
        className='filter-options-container'
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1100px',
          maxHeight: '90vh',
          overflow: 'hidden',
        }}
      >
        <div className='filter-options-header'>
          <div className='filter-options-header-content'>
            <div className='filter-options-header-icon'>
              <FilterListIcon sx={{ fontSize: 24, color: 'var(--action-color)' }} />
            </div>
            <div className='filter-options-header-text-group'>
              <div className='filter-options-header-text-primary'>
                Hit Filters
              </div>
              <div className='filter-options-header-text-secondary'>
                Narrow matches by BITE Lens, list name, entity country, and
                match score.
              </div>
            </div>
          </div>
          <div className='filter-options-header-close' onClick={onClose}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='var(--card-text)'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>

        <div className='filter-options-body-container'>
          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>BITE Lens</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {allLensOptions.map((option) =>
                renderOption(option, selectedLenses.includes(option), () =>
                  handleToggle(option, setSelectedLenses),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>List Name</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {listNameOptions.map((option) =>
                renderOption(option, selectedListNames.includes(option), () =>
                  handleToggle(option, setSelectedListNames),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>Entity Country</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {entityCountryOptions.map((option) =>
                renderOption(option, selectedCountries.includes(option), () =>
                  handleToggle(option, setSelectedCountries),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <div className='filter-column-header-title'>Match Score</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <label className='filter-column-header-title'>
                Minimum %
                <input
                  type='number'
                  min={0}
                  max={100}
                  value={minScore}
                  placeholder='Any'
                  className='tabs-filters-input'
                  onChange={(event) => setMinScore(event.target.value)}
                />
              </label>
              <label className='filter-column-header-title'>
                Maximum %
                <input
                  type='number'
                  min={0}
                  max={100}
                  value={maxScore}
                  placeholder='Any'
                  className='tabs-filters-input'
                  onChange={(event) => setMaxScore(event.target.value)}
                />
              </label>
            </div>
          </div>
        </div>

        <div className='filter-options-btns-container'>
          <div className='filter-options-btns-spacer'>
            <Button sx={{ color: 'var(--text-primary)' }} onClick={handleClear}>
              Clear
            </Button>
            <Button
              sx={{ color: 'var(--text-primary)', background: 'var(--action-color)' }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EntityHitsFilterModal;
